import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  BookOpen, 
  Users, 
  Workflow, 
  Database, 
  DollarSign,
  Building2,
  Briefcase,
  UserCheck,
  Landmark,
  Shield,
  Calendar,
  MessageSquare,
  FileText,
  Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MermaidDiagram } from "@/components/ui/MermaidDiagram";

const sections = [
  { id: "overview", label: "Vue d'ensemble", icon: BookOpen },
  { id: "pipeline", label: "Pipeline 11 étapes", icon: Workflow },
  { id: "roles", label: "Rôles & Relations", icon: Users },
  { id: "sellers", label: "Vendeurs", icon: Building2 },
  { id: "buyers", label: "Acheteurs", icon: Briefcase },
  { id: "brokers", label: "Brokers", icon: UserCheck },
  { id: "consultants", label: "Consultants", icon: Users },
  { id: "lenders", label: "Prêteurs", icon: Landmark },
  { id: "experts", label: "Nos Experts", icon: Shield },
  { id: "systems", label: "Systèmes Transversaux", icon: Calendar },
  { id: "economics", label: "Modèle Économique", icon: DollarSign },
  { id: "database", label: "Architecture DB", icon: Database },
];

export default function ReferencePage() {
  const [activeSection, setActiveSection] = useState("overview");

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-card/95 backdrop-blur">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-primary">AcquizGroup</h1>
              <p className="text-sm text-muted-foreground">Guide de Référence v1.1</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">FR</span>
          </div>
        </div>
      </header>

      <div className="container flex gap-6 py-6">
        {/* Sidebar Navigation */}
        <aside className="hidden md:block w-64 shrink-0">
          <ScrollArea className="h-[calc(100vh-8rem)]">
            <nav className="space-y-1 pr-4">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeSection === section.id
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {section.label}
                  </button>
                );
              })}
            </nav>
          </ScrollArea>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <ScrollArea className="h-[calc(100vh-8rem)]">
            <div className="pr-4 space-y-8">
              {activeSection === "overview" && <OverviewSection />}
              {activeSection === "pipeline" && <PipelineSection />}
              {activeSection === "roles" && <RolesSection />}
              {activeSection === "sellers" && <SellersSection />}
              {activeSection === "buyers" && <BuyersSection />}
              {activeSection === "brokers" && <BrokersSection />}
              {activeSection === "consultants" && <ConsultantsSection />}
              {activeSection === "lenders" && <LendersSection />}
              {activeSection === "experts" && <ExpertsSection />}
              {activeSection === "systems" && <SystemsSection />}
              {activeSection === "economics" && <EconomicsSection />}
              {activeSection === "database" && <DatabaseSection />}
            </div>
          </ScrollArea>
        </main>
      </div>
    </div>
  );
}

// ============= SECTIONS =============

function OverviewSection() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Vue d'ensemble de la Plateforme</h2>
        <p className="text-muted-foreground">
          AcquizGroup est une plateforme de mise en relation spécialisée dans les transactions M&A 
          (Mergers & Acquisitions). Elle connecte vendeurs, acheteurs, et professionnels via un 
          écosystème structuré autour d'un pipeline de 11 étapes.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Mission</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Standardiser les processus de vente et d'acquisition
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Offrir un préfinancement structuré avant la signature du LOI
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Garantir la confidentialité via l'anonymisation
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              Automatiser les workflows transactionnels
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Architecture des Usagers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-semibold">Type</th>
                  <th className="text-left py-2 font-semibold">Rôles</th>
                  <th className="text-left py-2 font-semibold">Caractéristiques</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 font-medium text-blue-600">INTERNES</td>
                  <td className="py-2">Admin, Staff, Prêteurs, Avocats, CPA, Notaires</td>
                  <td className="py-2">Accès gratuit, Success fees</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium text-green-600">EXTERNES</td>
                  <td className="py-2">Vendeurs, Acheteurs, Consultants</td>
                  <td className="py-2">Paiements forfaitaires ou transactionnels</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium text-purple-600">HYBRIDES</td>
                  <td className="py-2">Brokers</td>
                  <td className="py-2">Commission 1% sur AcquizGroup fees</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PipelineSection() {
  const pipelineChart = `graph TD
    subgraph Phase1["PHASE 1: SOUMISSION"]
        A["1. Soumission Vendeur"] --> B["2. Pre-evaluation"]
        B --> C["3. Creation CIM"]
    end
    
    subgraph Phase2["PHASE 2: PUBLICATION"]
        C --> D["4. Facturation"]
        D --> E["5. Publication"]
        E --> F["6. Offres Preteurs"]
    end
    
    subgraph Phase3["PHASE 3: NEGOCIATION"]
        F --> G["7. Signature NDA"]
        G --> H["8. Mise en Relation"]
        H --> I["9. Visioconference"]
    end
    
    subgraph Phase4["PHASE 4: CLOSING"]
        I --> J["10. Signature LOI"]
        J --> K["11. Financement"]
    end`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Pipeline Transactionnel (11 Étapes)</h2>
        <p className="text-muted-foreground">
          Chaque transaction suit un workflow standardisé garantissant traçabilité et conformité.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Diagramme du Pipeline</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={pipelineChart} />
        </CardContent>
      </Card>

      {/* Steps Details */}
      <div className="grid gap-4 md:grid-cols-2">
        {pipelineSteps.map((step, index) => (
          <Card key={index} className={`border-l-4 ${step.color}`}>
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
                  {index + 1}
                </div>
                <div>
                  <h4 className="font-semibold">{step.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

const pipelineSteps = [
  { title: "Soumission Vendeur", description: "Création profil, questionnaire, documents financiers, paiement $300", color: "border-blue-500" },
  { title: "Pré-évaluation", description: "Analyse financière automatique, redirection Admin/Staff", color: "border-blue-500" },
  { title: "Création CIM", description: "Génération Confidential Information Memorandum", color: "border-blue-500" },
  { title: "Facturation Complète", description: "Notification vendeur, invoice $2,449", color: "border-orange-500" },
  { title: "Publication Deal Monitor", description: "Annonce anonymisée, statut 'En financement'", color: "border-orange-500" },
  { title: "Soumission Prêteurs", description: "Envoi aux prêteurs, collecte des offres", color: "border-orange-500" },
  { title: "Signature NDA", description: "Acheteur signe, déblocage CIM et financiers", color: "border-green-500" },
  { title: "Mise en Relation", description: "Contact direct vendeur-acheteur", color: "border-green-500" },
  { title: "Visioconférence", description: "Rencontre avec enregistrement et transcription", color: "border-green-500" },
  { title: "Signature LOI", description: "Letter of Intent, déblocage prêteurs et avocats", color: "border-pink-500" },
  { title: "Financement & Closing", description: "Sélection prêteur, avocat, finalisation", color: "border-pink-500" },
];

function RolesSection() {
  const rolesChart = `graph TB
    subgraph Platform["ACQUIZGROUP PLATFORM"]
        Admin["Admin"]
        Staff["Staff"]
    end
    
    subgraph Internes["EXPERTS INTERNES"]
        Avocat["Avocats"]
        CPA["CPA"]
        Notaire["Notaires"]
        Tax["Tax Advisors"]
    end
    
    subgraph Externes["UTILISATEURS EXTERNES"]
        Seller["Vendeurs"]
        Buyer["Acheteurs"]
        Consultant["Consultants"]
    end
    
    subgraph Hybrides["HYBRIDES"]
        Broker["Brokers"]
        Lender["Preteurs"]
    end
    
    Seller -->|"Soumet deal"| Staff
    Staff -->|"Approuve"| Seller
    Buyer -->|"Signe NDA"| Seller
    Broker -->|"Represente"| Seller
    Lender -->|"Finance"| Buyer
    Consultant -->|"Conseille"| Buyer
    Avocat -->|"Post-LOI"| Buyer`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Rôles & Relations</h2>
        <p className="text-muted-foreground">
          Visualisation des interactions entre les différents acteurs de la plateforme.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Diagramme des Relations</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={rolesChart} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Matrice des Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Action</th>
                  <th className="text-center py-2">Visiteur</th>
                  <th className="text-center py-2">Bronze</th>
                  <th className="text-center py-2">Élite</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2">Voir Deal Monitor</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Signer NDA</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Contacter Vendeur</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">✅</td>
                  <td className="text-center py-2">✅</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Soumettre LOI</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">✅</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Voir Offres Prêteurs</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">✅</td>
                </tr>
                <tr>
                  <td className="py-2">Sélectionner Avocat</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">❌</td>
                  <td className="text-center py-2">✅</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SellersSection() {
  const sellerWorkflow = `graph LR
    A["1. Creation Profil"] --> B["2. Questionnaire"]
    B --> C["3. Documents 3 ans"]
    C --> D["4. Paiement 300$"]
    D --> E{"5. Evaluation"}
    E -->|"Approuve"| F["6. Paiement 2449$"]
    E -->|"Rejete"| G["Fin"]
    F --> H["7. Publication"]`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Vendeurs</h2>
        <p className="text-muted-foreground">
          Propriétaires d'entreprises souhaitant vendre leur société via la plateforme.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Coût d'accès
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <li><strong>$300</strong> - Pré-évaluation (à la soumission)</li>
              <li><strong>$2,449</strong> - Frais après approbation</li>
              <li className="text-primary font-semibold">TOTAL: $2,749</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Onglets Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              <li>📊 Dashboard - Évolution du deal</li>
              <li>📬 Boîte de Réception - Messages</li>
              <li>📁 Mes Documents - Boîte sécurisée</li>
              <li>🤝 Mes Partenaires - Internes/externes</li>
              <li>👥 Mes Consultants - Répertoire</li>
              <li>📅 Calendrier - RDVs synchronisés</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflow d'Onboarding</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={sellerWorkflow} />
        </CardContent>
      </Card>
    </div>
  );
}

function BuyersSection() {
  const buyerWorkflow = `graph TD
    A["Consulte Deal Monitor"] --> B{"Forfait?"}
    B -->|"Visiteur"| C["Vue seule + Favoris"]
    B -->|"Bronze/Elite"| D["Signe NDA"]
    D --> E["Recoit CIM + Financiers"]
    E --> F["Demande Contact Vendeur"]
    F --> G["Visioconference"]
    G --> H["Envoie LOI"]
    H --> I{"LOI Accepte?"}
    I -->|"Oui"| J["Acces Preteurs + Avocats"]
    I -->|"Non"| K["Negociation"]
    J --> L["Closing"]`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Acheteurs</h2>
        <p className="text-muted-foreground">
          Personnes physiques ou morales cherchant à acquérir une entreprise.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Forfaits Disponibles</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Forfait</th>
                  <th className="text-left py-2">Coût</th>
                  <th className="text-left py-2">Fonctionnalités</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 font-medium">🆓 Visiteur</td>
                  <td className="py-2">Gratuit</td>
                  <td className="py-2">Deal Monitor (vue), Favoris ❤, Répertoire consultants</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">📦 BYOD</td>
                  <td className="py-2">Par transaction</td>
                  <td className="py-2">Soumission deal personnel + évaluation + financement</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">🥉 Bronze</td>
                  <td className="py-2">Annuel</td>
                  <td className="py-2">NDA, Contact vendeurs, Consultants, Prêteurs</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">💎 Élite</td>
                  <td className="py-2">Annuel</td>
                  <td className="py-2">Bronze + Rapport marché/concurrence complet</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium">🌍 Immigration</td>
                  <td className="py-2">Add-on</td>
                  <td className="py-2">Accès avocats immigration (ajout Élite)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Parcours Acheteur</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={buyerWorkflow} />
        </CardContent>
      </Card>
    </div>
  );
}

function BrokersSection() {
  const brokerWorkflow = `graph LR
    A["1. Inscription"] --> B["2. 4 Clauses NDA"]
    B --> C["3. Background Check"]
    C --> D{"4. Approbation"}
    D -->|"Approuve"| E["5. Dashboard Actif"]
    D -->|"Rejete"| F["Fin"]
    E --> G["6. Soumet Deals"]
    G --> H["7. Gere Pipeline"]
    H --> I["8. Commission Closing"]`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Brokers</h2>
        <p className="text-muted-foreground">
          Intermédiaires mandatés par des vendeurs pour représenter leur entreprise.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Coût d'accès
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-green-600">Gratuit</p>
            <p className="text-sm text-muted-foreground mt-2">
              Success Fee de 1% sur les AcquizGroup fees au closing
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Onglets Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              <li>📊 Dashboard - Vue Kanban des deals</li>
              <li>🏭 Mes Vendeurs - Mandats actifs</li>
              <li>💼 Mes Acheteurs - Contacts intéressés</li>
              <li>💬 Messages - Communication</li>
              <li>📁 Documents - Contrats, mandats, NDAs</li>
              <li>🤝 Partenaires - Réseau professionnel</li>
              <li>📅 Calendrier - Google/Outlook sync</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflow Broker</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={brokerWorkflow} />
        </CardContent>
      </Card>
    </div>
  );
}

function ConsultantsSection() {
  const consultantWorkflow = `graph TD
    A["1. Inscription 4 etapes"] --> B["2. Expertise + Certifications"]
    B --> C["3. Taux Horaire"]
    C --> D["4. Activation"]
    D --> E["Dashboard Actif"]
    E --> F["Recoit Demandes"]
    E --> G["Ajoute Clients"]
    E --> H["Cree Mandats"]
    F --> I["Accompagnement"]
    G --> I
    H --> I`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Consultants</h2>
        <p className="text-muted-foreground">
          Conseillers stratégiques externes accompagnant acheteurs/vendeurs dans leur parcours.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Coût d'accès</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold">Abonnement Annuel</p>
            <p className="text-sm text-muted-foreground mt-2">
              Accès répertoire + prise de RDV + visibilité
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Onglets Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              <li>📊 Dashboard - KPIs performance</li>
              <li>👥 Mes Clients - Acheteurs/Vendeurs</li>
              <li>➕ Nouveau Client - Formulaire</li>
              <li>📋 Mes Mandats - Contrats actifs</li>
              <li>📝 Nouveau Mandat - Templates</li>
              <li>🤝 Partenariats - Collaborations</li>
              <li>📅 Calendrier - Disponibilités</li>
              <li>📈 Analytics - Statistiques</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflow Consultant</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={consultantWorkflow} />
        </CardContent>
      </Card>
    </div>
  );
}

function LendersSection() {
  const lenderWorkflow = `graph TD
    A["1. Inscription + Google Maps"] --> B["2. Criteres Pret"]
    B --> C["3. Background Check"]
    C --> D{"4. Approbation"}
    D -->|"Approuve"| E["Dashboard Actif"]
    D -->|"Rejete"| F["Fin"]
    E --> G["Notifications Deals"]
    G --> H["Consulte CIM"]
    H --> I["Soumet Offre"]
    I --> J{"LOI Signe?"}
    J -->|"Non"| K["Attend"]
    J -->|"Oui"| L["Contact Direct"]
    L --> M{"Selectionne?"}
    M -->|"Oui"| N["RDV + Financement"]
    M -->|"Non"| O["Deal perdu"]`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Prêteurs (Lenders)</h2>
        <p className="text-muted-foreground">
          Institutions financières offrant du préfinancement aux deals approuvés.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Coût d'accès</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-semibold text-green-600">Gratuit</p>
            <p className="text-sm text-muted-foreground mt-2">
              Success Fee de 2% sur les gains du prêteur
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Onglets Dashboard</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              <li>📊 Dashboard - Opportunités actives</li>
              <li>🎯 Deals Éligibles - Filtrés par critères</li>
              <li>📤 Mes Offres - Pending/Accepted/Rejected</li>
              <li>💬 Messages - Post-LOI uniquement</li>
              <li>📅 Calendrier - RDVs acheteurs</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflow Prêteur</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={lenderWorkflow} />
        </CardContent>
      </Card>
    </div>
  );
}

function ExpertsSection() {
  const expertsWorkflow = `graph LR
    A["LOI Signe"] --> B["Notification Auto"]
    B --> C["Expert Accepte"]
    C --> D["Acces Documents"]
    D --> E["RDV Calendrier"]
    E --> F["Accompagnement"]
    F --> G["Success Fee"]`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Nos Experts (Partenaires Internes)</h2>
        <p className="text-muted-foreground">
          Spécialistes internes disponibles après signature du LOI.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          { icon: "⚖️", title: "Avocats d'affaires", desc: "Contrats, due diligence juridique" },
          { icon: "🌍", title: "Avocats Immigration", desc: "Visas investisseurs, permis travail" },
          { icon: "📝", title: "Notaires", desc: "Actes authentiques, closing" },
          { icon: "📊", title: "CPA", desc: "Audit financier, comptabilité" },
          { icon: "💰", title: "Tax Advisors", desc: "Optimisation fiscale, structuration" },
        ].map((expert, i) => (
          <Card key={i} className="border-l-4 border-blue-500">
            <CardContent className="pt-4">
              <div className="text-2xl mb-2">{expert.icon}</div>
              <h4 className="font-semibold">{expert.title}</h4>
              <p className="text-sm text-muted-foreground">{expert.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflow Experts</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={expertsWorkflow} />
        </CardContent>
      </Card>
    </div>
  );
}

function SystemsSection() {
  const systemsChart = `graph TB
    subgraph Frontend["FRONTEND"]
        React["React + TypeScript"]
        Tailwind["Tailwind CSS"]
        Shadcn["shadcn/ui"]
    end
    
    subgraph Auth["AUTHENTIFICATION"]
        Firebase["Firebase Auth"]
        Roles["NestJS user_roles"]
    end
    
    subgraph Backend["BACKEND"]
        Firestore["Firestore DB"]
        Storage["Firebase Storage"]
        Functions["Cloud Functions"]
        NestAPI["NestJS API (Cloud Run)"]
    end
    
    subgraph External["SERVICES EXTERNES"]
        Stripe["Stripe Payments"]
        Jitsi["Jitsi Meet"]
        Twilio["Twilio Calls"]
        GCal["Google Calendar"]
    end
    
    React --> Firebase
    React --> Firestore
    React --> NestAPI
    Functions --> Stripe
    Functions --> Twilio
    React --> Jitsi
    React --> GCal`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Systèmes Transversaux</h2>
        <p className="text-muted-foreground">
          Fonctionnalités partagées entre tous les rôles.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Authentification Multi-Rôles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              <li>• Firebase Auth + NestJS user_roles</li>
              <li>• Un utilisateur peut avoir plusieurs rôles</li>
              <li>• RoleSwitcher dans le header</li>
              <li>• Navigation rapide entre dashboards</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Système de Calendrier
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              <li>• Sync bidirectionnelle Google/Outlook</li>
              <li>• VisualCalendar dans tous les dashboards</li>
              <li>• Disponibilités staff sur /contact</li>
              <li>• Prise de RDV intégrée</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Messagerie Temps Réel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              <li>• Conversations par deal</li>
              <li>• Notifications multi-canal</li>
              <li>• Historique Firestore</li>
              <li>• Transcriptions visio automatiques</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Système de Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              <li>• Signature électronique (NDA, LOI)</li>
              <li>• Génération PDF automatique</li>
              <li>• Stockage sécurisé Firebase Storage</li>
              <li>• Historique des versions</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Architecture Système</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={systemsChart} />
        </CardContent>
      </Card>
    </div>
  );
}

function EconomicsSection() {
  const economicsChart = `graph TD
    A["Transaction 1M$"] --> B["Vendeur paie 2749$ + 1%"]
    A --> C["Acheteur paie Abonnement + 1%"]
    B --> D["AcquizGroup Revenus"]
    C --> D
    D --> E["Broker: 1%"]
    D --> F["Preteur: 2%"]
    D --> G["Experts: Success fees"]`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Modèle Économique</h2>
        <p className="text-muted-foreground">
          Sources de revenus et structure tarifaire de la plateforme.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sources de Revenus</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Source</th>
                  <th className="text-left py-2">Montant</th>
                  <th className="text-left py-2">Conditions</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 font-medium">Vendeurs</td>
                  <td className="py-2">$2,749 + 1% fee</td>
                  <td className="py-2">$300 pré-éval + $2,449 post-approbation</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Acheteurs Bronze</td>
                  <td className="py-2">Annuel + 1% fee</td>
                  <td className="py-2">Accès NDA + Deal Monitor</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Acheteurs Élite</td>
                  <td className="py-2">Annuel + 1% fee</td>
                  <td className="py-2">Accès complet + rapport marché</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Consultants</td>
                  <td className="py-2">Annuel</td>
                  <td className="py-2">Accès répertoire + RDV</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Prêteurs</td>
                  <td className="py-2">2% success fee</td>
                  <td className="py-2">Prélevé sur gains prêteur</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 font-medium">Brokers</td>
                  <td className="py-2">1% success fee</td>
                  <td className="py-2">Sur AcquizGroup fees</td>
                </tr>
                <tr>
                  <td className="py-2 font-medium">Partenaires</td>
                  <td className="py-2">$200/mois + 1%</td>
                  <td className="py-2">Sur transactions closes</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Flux Financier</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={economicsChart} />
        </CardContent>
      </Card>
    </div>
  );
}

function DatabaseSection() {
  const dbChart = `erDiagram
    USERS ||--o{ DEALS : sells
    USERS ||--o{ DEALS : buys
    DEALS ||--o{ DEAL_DOCUMENTS : contains
    DEALS ||--o{ DEAL_PARTICIPANTS : involves
    DEALS ||--o{ LENDER_OFFERS : receives
    USERS ||--o{ USER_ROLES : has
    USERS ||--o{ FAVORITES : saves`;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold mb-2">Architecture Base de Données</h2>
        <p className="text-muted-foreground">
          Organisation des collections Firebase Firestore.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Collections Principales</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 md:grid-cols-2 text-sm">
            <div className="p-2 bg-muted rounded">
              <span className="font-mono font-semibold">users</span> - Profils multi-rôles
            </div>
            <div className="p-2 bg-muted rounded">
              <span className="font-mono font-semibold">deals</span> - Transactions pipeline
            </div>
            <div className="p-2 bg-muted rounded">
              <span className="font-mono font-semibold">consultants</span> - Répertoire
            </div>
            <div className="p-2 bg-muted rounded">
              <span className="font-mono font-semibold">brokers</span> - Mandats
            </div>
            <div className="p-2 bg-muted rounded">
              <span className="font-mono font-semibold">lenders</span> - Critères prêt
            </div>
            <div className="p-2 bg-muted rounded">
              <span className="font-mono font-semibold">messages</span> - Messagerie
            </div>
            <div className="p-2 bg-muted rounded">
              <span className="font-mono font-semibold">ndas / lois</span> - Documents signés
            </div>
            <div className="p-2 bg-muted rounded">
              <span className="font-mono font-semibold">lenderOffers</span> - Offres financement
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Relations Principales</CardTitle>
        </CardHeader>
        <CardContent>
          <MermaidDiagram chart={dbChart} />
        </CardContent>
      </Card>
    </div>
  );
}
